import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
var _CapturedStream, _AudioSignalContainer, _PipModal;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { Provider } from 'react-redux';
import ScreenCaptureEffects from './ScreenCaptureEffects';
import MediaOverlay from './MediaOverlay';
import CapturedStream from './CapturedStream';
import Controller from './Controller';
import AudioSignalContainer from './AudioSignalContainer';
import CapturePresentation from './CapturePresentation';
import MediaContainer from './MediaContainer';
import { getInitialState, reducer } from '../../reducers';
import { applyTranslations, TranslationPropTypes } from '../../constants/translated/translations';
import { devicesMiddleware, recordingMiddleware, recordingNavigationWarningMiddleware, soundMeterMiddleware } from '../../middlewares';
import { finishClicked, pauseClicked, continueClicked, closeClicked, disablePictureInPicture, enablePictureInPicture } from '../../actions';
import PreviewScreen from './PreviewScreen';
import pictureInPictureMiddleware from '../../middlewares/pictureInPictureMiddleware';
import PipModal from './PipModal';
const TranslationsPropType = PropTypes.shape(TranslationPropTypes);
function ScreenCapture({
  actionSetterCallback,
  onCancel,
  onChange,
  onCompleted,
  onModeChange,
  translations,
  noScreenSharing,
  onTimeChange,
  screenSharingBetaActionText,
  screenSharingBetaActionURL,
  onStreamInitialized,
  onStartoverClicked,
  onPipChanged,
  experimentalScreenShareOptions,
  disableInMemoryRecording,
  streamConfigOverrides,
  onError,
  systemPermissionsURL
}) {
  const store = useMemo(() => {
    const initialState = getInitialState();
    return createStore(reducer, _objectSpread(_objectSpread({}, initialState), {}, {
      disableInMemoryRecording
    }), applyMiddleware(thunk, devicesMiddleware({
      streamConfigOverrides
    }), recordingMiddleware({
      onStreamInitialized
    }), recordingNavigationWarningMiddleware, soundMeterMiddleware, pictureInPictureMiddleware));
  }, []); // no deps to resemble constructor behavior

  useEffect(() => {
    applyTranslations(translations);
  }, []);
  useEffect(() => {
    actionSetterCallback({
      togglePip: () => {
        store.getState().pipEnabled ? store.dispatch(disablePictureInPicture()) : store.dispatch(enablePictureInPicture());
      },
      closeClicked: () => store.dispatch(closeClicked()),
      finishClicked: () => store.dispatch(finishClicked()),
      pauseClicked: () => store.dispatch(pauseClicked()),
      continueClicked: () => store.dispatch(continueClicked())
    });
  }, [store, actionSetterCallback]);
  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(CapturePresentation, null, /*#__PURE__*/React.createElement(ScreenCaptureEffects, {
    onChange: onChange,
    onCompleted: onCompleted,
    onCancel: onCancel,
    onModeChange: onModeChange,
    onTimeChange: onTimeChange,
    onPipChanged: onPipChanged,
    experimentalScreenShareOptions: experimentalScreenShareOptions,
    onError: onError
  }), /*#__PURE__*/React.createElement(MediaContainer, null, _CapturedStream || (_CapturedStream = /*#__PURE__*/React.createElement(CapturedStream, null)), /*#__PURE__*/React.createElement(MediaOverlay, {
    systemPermissionsLink: systemPermissionsURL
  }), _AudioSignalContainer || (_AudioSignalContainer = /*#__PURE__*/React.createElement(AudioSignalContainer, null))), /*#__PURE__*/React.createElement(Controller, {
    noScreenSharing: noScreenSharing,
    screenSharingBetaActionText: screenSharingBetaActionText,
    screenSharingBetaActionURL: screenSharingBetaActionURL,
    onStartoverClicked: onStartoverClicked
  }), _PipModal || (_PipModal = /*#__PURE__*/React.createElement(PipModal, null))));
}
ScreenCapture.propTypes = {
  /**
   * Callback fired when a file has been generated.
   */
  onCompleted: PropTypes.func.isRequired,
  /**
   * Callback fired when the component is closed for a reason
   * other than generating a file. Includes the last
   * emitted state when the action was taken.
   */
  onCancel: PropTypes.func,
  /**
   * Callback fired when the component state is changed
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when recording mode is changed
   * Possible values: 'screensharing', 'webcam', 'audioonly', 'inactive'
   */
  onModeChange: PropTypes.func,
  /**
   * Callback fired when recording time is changed
   */
  onTimeChange: PropTypes.func,
  /**
   * Label overrides for i18n. Defaults to english
   * See src/constants/translated/translations.js for default values
   */
  translations: TranslationsPropType,
  /**
   * Callback for setting action handlers.
   * Used for dispatching actions from outside ScreenCapture.
   */
  actionSetterCallback: PropTypes.func,
  /**
   * Removes screen sharing request button.
   */
  noScreenSharing: PropTypes.bool,
  /**
   * Screen sharing Beta indication link text.
   */
  screenSharingBetaActionText: PropTypes.string,
  /**
   * Screen sharing Beta indication url.
   */
  screenSharingBetaActionURL: PropTypes.string,
  onStreamInitialized: PropTypes.func,
  onStartoverClicked: PropTypes.func,
  onPipChanged: PropTypes.func,
  experimentalScreenShareOptions: PropTypes.object,
  disableInMemoryRecording: PropTypes.bool,
  streamConfigOverrides: PropTypes.object,
  /**
   * Callback fired when error is added to redux store.
   * It receives error string as a param
   */
  onError: PropTypes.func,
  /**
   * Link to the guide which is suggested if no capture device
   * is permitted on the system level.
   */
  systemPermissionsURL: PropTypes.string
};
ScreenCapture.defaultProps = {
  onCancel: () => {},
  onChange: () => {},
  onModeChange: () => {},
  translations: {},
  actionSetterCallback: () => {},
  noScreenSharing: false,
  onTimeChange: () => {},
  screenSharingBetaActionText: '',
  screenSharingBetaActionURL: '',
  onStreamInitialized: () => {},
  onStartoverClicked: () => {},
  onPipChanged: () => {},
  experimentalScreenShareOptions: {},
  disableInMemoryRecording: false,
  streamConfigOverrides: {},
  onError: () => {},
  systemPermissionsURL: ''
};
export default ScreenCapture;
export { PreviewScreen };